// External globals
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { Codes } from '@/constants/codes';
import { AccessAuthorities } from '@/models';
import moment from 'moment';
import { hasValue, getModuleType, currentUserHasPermission } from '@/utils/legacy-utils';
import { cancelPanicDialog } from '@/models/legacy/notification-dialogs';

import { ModuleType } from '@/constants/codes';
import { hasNumericValue } from '@/utils/validation';
import { getCurrentUserId } from '@/utils/auth';
import { CallCardStatus } from '@/services/api-types';
import { load } from '@/services/loading';
import { isUWPApp, isIOSApp, isAndroidApp, isWindowsAppSdkApp, kstConfirm, kstPrompt, CallCard, Mousetrap, $, DATETIME_FORMAT_SERVER_UTC } from '@/utils/window-globals';
// Lazy window-global lookups — resolved at call time, not module load time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getMDTActiveCallCardId = _wg('getMDTActiveCallCardId');

export function CAD_PanicButton() {
}

export function MDT_PanicButton() {
    if (isMDTPanicButtonBlinking() && hasNumericValue(getMDTActiveCallCardId())) {
        const container = document.createElement('div');
        document.body.appendChild(container);

        function cleanup() {
            (window as any).OrnaReact.destroyComponent(container);
            if (container.parentNode) {
                container.parentNode.removeChild(container);
            }
        }

        (window as any).OrnaReact.renderComponent(
            (window as any).PanicButtonModalReact,
            {
                onGoToCurrentCall: function () {
                    cleanup();
                    goToCurrentMDTCall();
                },
                onCreateNewCall: function () {
                    cleanup();
                    createNewPanicCall();
                },
                onCancel: function () {
                    cleanup();
                }
            },
            container
        );
    } else {
        createNewPanicCall();
    }
}

export function createNewPanicCall() {
    cancelPanicDialog.loadActiveCallCardPriorToPanicCall();

    api.saveCallCardFromPanicButton((result) => {
        //Skip dialog asking if we want to continue editing current page if in edit mode on that page.
        App.pauseItemChangeCheck = true;
        //Overwrite existing active call since the user might be forced off another call.
        goToSelfInitiatedCall(result.id, true);
        setMDTPanicButtonBlinkState(result.id, true);

        setTimeout(() => {
            cancelPanicDialog.setPanicDialogCallCardId(result.id);
            cancelPanicDialog.show();
        }, 0)
    }, () => {
        App.showPopupNotification("Failed to create Panic call.", "error");
    })
}

export let _isMDTPanicButtonBlinking = false;


export function isMDTPanicButtonBlinking() {
    return _isMDTPanicButtonBlinking;
}

export let _mdtPanicButtonCallCardsBlinkingArr: Array<number> = []
export function setMDTPanicButtonBlinkState(callCardId: number, isBlinking: boolean) {
    const isCallCardIdCurrentlyBlinking: boolean = isInArray(_mdtPanicButtonCallCardsBlinkingArr, callCardId);
    //If the panic button isn't set to the panic state for callCardId, set the state that it should be blinking.
    if (isBlinking && !isCallCardIdCurrentlyBlinking) {
        _mdtPanicButtonCallCardsBlinkingArr.push(callCardId);
    } else if (!isBlinking && isCallCardIdCurrentlyBlinking) {
        //If the panic button is set to the panic state for callCardId, set the state that it shouldn't be blinking.
        removeFromArray(_mdtPanicButtonCallCardsBlinkingArr, callCardId);
    }
    setMDTPanicButtonBlinking(_mdtPanicButtonCallCardsBlinkingArr.length > 0);
}

export function panicCallOfOfficer(callback: Function) {
    const currentUserId: number = getCurrentUserId();
    api.getPanicCallCards(1, '10000', result => {
        for (let row of result.rows) {
            if (row.primaryOfficer === currentUserId) {
                if (row.primaryStatus !== 'c') {
                    callback(row.id);
                    return;
                }
            }
            if (hasValue(row.backupUnits)) {
                for (let backupUnit of row.backupUnits) {
                    if (backupUnit.officer === currentUserId && backupUnit.status !== 'c') {
                        callback(row.id);
                        return;
                    }
                }
            }
        }
        callback(null);
    }, () => {
        //Only show this notification if we're signed in.
        if (getAuthObject()) {
            App.showPopupNotification("Error detecting if panic call is active", "error");
        }
    }, true);
}

export function setMDTPanicButtonBlinkingFromServerState() {
    panicCallOfOfficer((callCardId) => {
        if (hasValue(callCardId)) {
            _mdtPanicButtonCallCardsBlinkingArr = [callCardId]
            setMDTPanicButtonBlinking(true);
        } else {
            _mdtPanicButtonCallCardsBlinkingArr = []
            setMDTPanicButtonBlinking(false);
        }
    });
}

export function setMDTPanicButtonBlinking(isBlinking: boolean) {
    _isMDTPanicButtonBlinking = isBlinking;
    const el = document.getElementById('PanicButton');
    if (el) {
        el.style.animation = isBlinking ? "panic-button-blink 1s linear infinite" : "none";
    }
}

export function setCADPanicButtonBlinking(isBlinking: boolean) {
    const el = document.getElementById('InProgressPanic');
    if (el) {
        el.style.animation = isBlinking ? "panic-button-blink 1s linear infinite" : "none";
    }
}

export function isPanicButtonGloballyEnabled(): boolean {
    return localStorage.getItem("IsPanicButtonEnabled") == "true"
}

export function updatePanicButtonState(isPanicButtonEnabled: boolean) {
    if (!hasValue(isPanicButtonEnabled)) {
        localStorage.removeItem("IsPanicButtonEnabled")
    } else {
        localStorage.setItem("IsPanicButtonEnabled", isPanicButtonEnabled.toString());
    }
}

export function goToCurrentMDTCall() {
    const callCardId: number = getMDTActiveCallCardId();
    if (!hasNumericValue(callCardId)) {
        App.showPopupNotification("Error finding current call.", "error");
        return;
    }
    window.OrnaReact.navigate.toCallCard(callCardId, 'MDT');
}

//The number of call card IDs should be very small, usually 1, so use an array instead of an object/map to store the state.
// We need to store the state to handle multiple calls in case there are multiple simultaneous panic calls.
//_cadPanicButtonCallCardsBlinkingArr stores an array of call card IDs that are in the panic state. This array having a length > 0 means
// that at least one call card has the panic NOC, so the panic button at the top left should be blinking for people in the CAD module.
export let _cadPanicButtonCallCardsBlinkingArr: Array<number> = []
export function setCADPanicButtonBlinkState(callCardId: number, isBlinking: boolean) {
    const isCallCardIdCurrentlyBlinking: boolean = isInArray(_cadPanicButtonCallCardsBlinkingArr, callCardId);
    //If the panic button isn't set to the panic state for callCardId, set the state that it should be blinking.
    if (isBlinking && !isCallCardIdCurrentlyBlinking) {
        _cadPanicButtonCallCardsBlinkingArr.push(callCardId);
    } else if (!isBlinking && isCallCardIdCurrentlyBlinking) {
        //If the panic button is set to the panic state for callCardId, set the state that it shouldn't be blinking.
        removeFromArray(_cadPanicButtonCallCardsBlinkingArr, callCardId);
    }
    setCADPanicButtonBlinking(_cadPanicButtonCallCardsBlinkingArr.length > 0);
}

export function setCADPanicButtonBlinkStateFromServer() {
    api.getPanicCallCards(1, '10000', result => {
        _cadPanicButtonCallCardsBlinkingArr = [];
        for (let row of result.rows) {
            const callCardId: number = row.id;
            _cadPanicButtonCallCardsBlinkingArr.push(callCardId);
        }
        setCADPanicButtonBlinking(_cadPanicButtonCallCardsBlinkingArr.length > 0);
    }, () => {
        //Only show this notification if we're signed in.
        if (getAuthObject()) {
            App.showPopupNotification("Error retreiving panic button state.", "error");
        }
    }, true);
}

// Window registrations
(window as any).MDT_PanicButton = MDT_PanicButton;
(window as any).CAD_PanicButton = typeof CAD_PanicButton !== 'undefined' ? CAD_PanicButton : undefined;
(window as any).setMDTPanicButtonBlinkState = setMDTPanicButtonBlinkState;
(window as any).setCADPanicButtonBlinkState = setCADPanicButtonBlinkState;
(window as any).setMDTPanicButtonBlinkingFromServerState = setMDTPanicButtonBlinkingFromServerState;
(window as any).setCADPanicButtonBlinkStateFromServer = setCADPanicButtonBlinkStateFromServer;
(window as any).isPanicButtonGloballyEnabled = isPanicButtonGloballyEnabled;
(window as any).updatePanicButtonState = updatePanicButtonState;
